import React from "react";
import { useTheme } from "../../../../contexts/ThemeContext";
import { ScreenRow } from "../../../../types";
import { useGetPlansQuery } from "../../../../services/subscription";
interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  selectedScreen?: ScreenRow | null;
}
// Fetch plans

const UpgradeModal: React.FC<UpgradeModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  selectedScreen,
}) => {
  const { theme } = useTheme();
  const { data: plansResp, isLoading: plansLoading } = useGetPlansQuery();
  const plans: any[] = Array.isArray((plansResp as any)?.results)
    ? (plansResp as any).results
    : Array.isArray((plansResp as any)?.data?.results)
    ? (plansResp as any).data.results
    : Array.isArray(plansResp)
    ? (plansResp as any[])
    : [];

  const matchedPlan = React.useMemo(() => {
    if (!selectedScreen || !Array.isArray(plans)) return null;
    // try a few common places the plan id might live on the selectedScreen
    const planId =
      (selectedScreen as any).planId ||
      (selectedScreen as any).plan?.id ||
      (selectedScreen as any).plan?._id ||
      (selectedScreen as any).plan;
    if (!planId) return null;
    return plans.find(
      (p) =>
        String((p as any)._id) === String(planId) ||
        String((p as any).id) === String(planId)
    );
  }, [plans, selectedScreen]);

  const priceObj = React.useMemo(() => {
    if (!matchedPlan?.prices?.length) return null;
    // prefer INR price if available, otherwise fallback to first available
    return (
      matchedPlan.prices.find((pr: any) => pr.currency === "INR") ||
      matchedPlan.prices[0] ||
      null
    );
  }, [matchedPlan]);

  const subtotal = Number(priceObj?.price ?? 0);
  const taxPercent = Number(priceObj?.tax?.percent ?? 0);
  const taxType = String(priceObj?.tax?.type ?? "EXCLUSIVE").toUpperCase();

  const taxAmount = React.useMemo(() => {
    if (!taxPercent) return 0;
    if (taxType === "INCLUSIVE") {
      // extract tax portion from an inclusive price
      return +(subtotal - subtotal / (1 + taxPercent / 100));
    }
    // exclusive: tax is added on top
    return +(subtotal * (taxPercent / 100));
  }, [subtotal, taxPercent, taxType]);

  const total = +(subtotal + taxAmount);

  const currencySymbols: Record<string, string> = {
    INR: "₹",
    USD: "$",
    AED: "د.إ",
  };
  const currency = priceObj?.currency ?? "INR";
  const currencySymbol = currencySymbols[currency] || currency;

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose}>
      <div className="flex-1" onClick={onClose} />
      <div
        className={`w-80 h-full ${
          theme === "dark" ? "bg-[#1F1F1F]" : "bg-white"
        } shadow-xl transform transition-transform duration-300 ease-in-out flex flex-col`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className={`p-4 border-b ${
            theme === "dark" ? "border-[#2A2A2A]" : "border-gray-200"
          } flex-shrink-0`}
        >
          <div className="flex items-center justify-between">
            <h3
              className={`text-lg font-semibold ${
                theme === "dark" ? "text-white" : "text-gray-900"
              }`}
            >
              Upgrade Screen to{" "}
              <span
                className={`${
                  theme === "dark" ? "text-[#B6CB24]" : "text-[#6D790F]"
                }`}
              >
                Premium
              </span>
            </h3>

            <button
              onClick={onClose}
              className={`p-2 rounded-full transition-colors ${
                theme === "dark" ? "hover:bg-white/10" : "hover:bg-black/5"
              }`}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M18.3 5.71 12 12l6.3 6.29-1.41 1.42L10.59 13.4 4.29 19.7 2.88 18.29 9.17 12 2.88 5.71 4.29 4.3l6.3 6.29 6.29-6.29z" />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex-1 p-4 flex flex-col justify-between">
          <div className="space-y-2">
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M3 12C7.5 12 12 7.5 12 3C12 7.5 16.5 12 21 12C16.5 12 12 16.5 12 21C12 16.5 7.5 12 3 12Z"
                    stroke="#C6D92C"
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M2 19.5C2.83333 19.5 4.5 17.8333 4.5 17C4.5 17.8333 6.16667 19.5 7 19.5C6.16667 19.5 4.5 21.1667 4.5 22C4.5 21.1667 2.83333 19.5 2 19.5Z"
                    stroke="#C6D92C"
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M16 5C17 5 19 3 19 2C19 3 21 5 22 5C21 5 19 7 19 8C19 7 17 5 16 5Z"
                    stroke="#C6D92C"
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div>
                <h4
                  className={`text-sm font-medium mb-1 ${
                    theme === "dark" ? "text-[#EDF5C0]" : "text-gray-900"
                  }`}
                >
                  Custom Logo, Color Options
                </h4>
                <p
                  className={`text-xs leading-relaxed ${
                    theme === "dark" ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Upload your gym’s logo and set unique color themes to match
                  your brand.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M3 12C7.5 12 12 7.5 12 3C12 7.5 16.5 12 21 12C16.5 12 12 16.5 12 21C12 16.5 7.5 12 3 12Z"
                    stroke="#C6D92C"
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M2 19.5C2.83333 19.5 4.5 17.8333 4.5 17C4.5 17.8333 6.16667 19.5 7 19.5C6.16667 19.5 4.5 21.1667 4.5 22C4.5 21.1667 2.83333 19.5 2 19.5Z"
                    stroke="#C6D92C"
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M16 5C17 5 19 3 19 2C19 3 21 5 22 5C21 5 19 7 19 8C19 7 17 5 16 5Z"
                    stroke="#C6D92C"
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div>
                <h4
                  className={`text-sm font-medium mb-1 ${
                    theme === "dark" ? "text-[#EDF5C0]" : "text-gray-900"
                  }`}
                >
                  Additional Gym Type
                </h4>
                <p
                  className={`text-xs leading-relaxed ${
                    theme === "dark" ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Unlock access to CrossFit and advanced training categories.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M3 12C7.5 12 12 7.5 12 3C12 7.5 16.5 12 21 12C16.5 12 12 16.5 12 21C12 16.5 7.5 12 3 12Z"
                    stroke="#C6D92C"
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M2 19.5C2.83333 19.5 4.5 17.8333 4.5 17C4.5 17.8333 6.16667 19.5 7 19.5C6.16667 19.5 4.5 21.1667 4.5 22C4.5 21.1667 2.83333 19.5 2 19.5Z"
                    stroke="#C6D92C"
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M16 5C17 5 19 3 19 2C19 3 21 5 22 5C21 5 19 7 19 8C19 7 17 5 16 5Z"
                    stroke="#C6D92C"
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div>
                <h4
                  className={`text-sm font-medium mb-1 ${
                    theme === "dark" ? "text-[#EDF5C0]" : "text-gray-900"
                  }`}
                >
                  Custom Workouts
                </h4>
                <p
                  className={`text-xs leading-relaxed ${
                    theme === "dark" ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Add your own workout videos and create personalized training
                  programs.
                </p>
              </div>
            </div>
          </div>

          {/* Cost Details */}
          <div className="mt-4">
            <h4
              className={`text-base font-semibold mb-4 ${
                theme === "dark" ? "text-white" : "text-gray-900"
              }`}
            >
              Cost Details
            </h4>

            <div className="space-y-3 text-sm">
              <div className="flex justify-between items-center">
                <span
                  className={`${
                    theme === "dark" ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Bill Date
                </span>
                <span
                  className={`${
                    theme === "dark" ? "text-white" : "text-gray-900"
                  }`}
                >
                  {(selectedScreen as any)?.billingDate ??
                    (selectedScreen as any)?.billDate ??
                    "30/09/2025"}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span
                  className={`${
                    theme === "dark" ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  End Date
                </span>
                <span
                  className={`${
                    theme === "dark" ? "text-white" : "text-gray-900"
                  }`}
                >
                  {(selectedScreen as any)?.endDate ??
                    (selectedScreen as any)?.expiryDate ??
                    "30/10/2025"}
                </span>
              </div>

              <hr
                className={`my-3 ${
                  theme === "dark" ? "border-gray-600" : "border-gray-300"
                }`}
              />

              <div className="flex justify-between items-center">
                <span
                  className={`${
                    theme === "dark" ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  {matchedPlan?.name ?? matchedPlan?.type ?? "Type"}
                </span>
                <span
                  className={`${
                    theme === "dark" ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  {currencySymbol}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span
                  className={`${
                    theme === "dark" ? "text-white" : "text-gray-900"
                  }`}
                >
                  Sub Total
                </span>
                <span
                  className={`${
                    theme === "dark" ? "text-white" : "text-gray-900"
                  }`}
                >
                  {currencySymbol}
                  {subtotal.toFixed(2)}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span
                  className={`${
                    theme === "dark" ? "text-white" : "text-gray-900"
                  }`}
                >
                  Taxes ({taxPercent}%{" "}
                  {taxType === "INCLUSIVE" ? "inc." : "exc."})
                </span>
                <span
                  className={`${
                    theme === "dark" ? "text-white" : "text-gray-900"
                  }`}
                >
                  {currencySymbol}
                  {taxPercent.toFixed(2)}
                </span>
              </div>

              <hr
                className={`my-3 ${
                  theme === "dark" ? "border-gray-600" : "border-gray-300"
                }`}
              />

              <div className="flex justify-between items-center font-semibold">
                <span
                  className={`${
                    theme === "dark" ? "text-white" : "text-gray-900"
                  }`}
                >
                  Total
                </span>
                <span
                  className={`${
                    theme === "dark" ? "text-white" : "text-gray-900"
                  }`}
                >
                  {currencySymbol}
                  {total.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Buttons */}
        <div
          className={`p-6 border-t space-y-2 flex-shrink-0 ${
            theme === "dark" ? "border-[#2A2A2A]" : "border-gray-200"
          }`}
        >
          <button
            onClick={onConfirm}
            className="w-full px-4 py-3 font-medium text-black rounded-[10px] border text-sm"
            style={{
              border: "1px solid var(--menu-active-border, #C6D92C)",
              background:
                "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.80) 0%, rgba(83, 91, 16, 0.80) 100%)",
            }}
          >
            Upgrade Now
          </button>
          <button
            onClick={onClose}
            className={`w-full px-4 py-3 font-medium rounded-[10px] border text-sm ${
              theme === "dark"
                ? "border-gray-600 text-gray-300 hover:bg-white/10"
                : "border-gray-300 text-gray-700 hover:bg-black/5"
            }`}
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpgradeModal;
